import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  CardHeader,
  CardContent,
  Divider,
  Grid,
  TextField,
  Button,
  Typography,
  Box,
  IconButton,
  Avatar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stepper,
  Step,
  StepLabel,
  Paper,
  CircularProgress,
  Switch,
  FormControlLabel,
  Radio,
  RadioGroup,
  FormLabel,
  Checkbox,
  FormGroup,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Tooltip
} from '@mui/material';
import { Tabs, Tab } from '@mui/material';
import {
  Add as AddIcon,
  ArrowBack as ArrowBackIcon,
  Contacts as ContactsIcon,
  Delete as DeleteIcon,
  LocalHospital as HealthIcon,
  Info as InfoIcon,
  LocationOn as LocationIcon,
  NavigateBefore as BackIcon,
  NavigateNext as NextIcon,
  Person as PersonIcon,
  Save as SaveIcon,
  School as SchoolIcon,
  Work as WorkIcon,
  ExpandMore as ExpandMoreIcon,
  HelpOutline as HelpIcon,
  Group as GroupIcon,
  SportsSoccer as SportsIcon,
  MenuBook as BookIcon
} from '@mui/icons-material';
import Swal from 'sweetalert2';
import { SwalConfig } from '../../utils/swalConfig';
import dayjs from 'dayjs';

// Import static data structure
import { candidatesData } from '../../Data/Kontak/Candidate';

const AddCandidate = () => {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [formMode, setFormMode] = useState('full'); // 'simple' | 'full'
  const [candidateFormData, setCandidateFormData] = useState({
    // Data Pribadi
    personalData: {
      full_name: '',
      gender: '',
      birth_place: '',
      birth_date: '',
      religion: '',
      ethnicity: '',
      age: '',
      height_cm: '',
      weight_kg: '',
      address: '',
      phone: '',
      id_number: '',
      id_region: '',
      living_status: '',
      vehicle_type: '',
      vehicle_ownership: '',
      best_achievement: '',
      best_achievement_desc: '',
      worst_achievement: '',
      worst_achievement_desc: '',
      language_active: '',
      language_passive: '',
      education_sponsor: '',
      scholarship: '',
      scholarship_duration: '',
      scholarship_desc: '',
      influence_source: '',
      influence_note: '',
      publication: '',
      highschool_avg_score: '',
      highschool_subject_count: '',
      gpa: '',
      gpa_max: '',
      photo: '',
      // SIM Data
      has_sim: false,
      sim_c_valid: false,
      sim_c_number: '',
      sim_c_valid_until: '',
      sim_a_valid: false,
      sim_a_number: '',
      sim_a_valid_until: '',
      sim_b_valid: false,
      sim_b_number: '',
      sim_b_valid_until: ''
    },
    
    // Riwayat
    history: {
      last_job_reason_leave: '',
      last_job_salary: '',
      last_job_benefits: '',
      job_most_liked: '',
      job_least_liked: '',
      hobby: '',
      sport: 0,
      sport_desc: '',
      free_time: '',
      reading_hours: '',
      reading_topics: '',
      newspaper: '',
      magazine: ''
    },
    
    // Riwayat Pekerjaan
    workHistory: [],
    
    // Aktivitas Sosial
    socialActivities: [],
    
    // Keluarga
    family: {
      marital_status: '',
      marriage_date: '',
      marriage_count: 0,
      members: []
    },
    
    // Lamaran dan Keterampilan
    applicationSkills: {
      apply_reason: '',
      apply_initiator: '',
      apply_initiator_note: '',
      desired_position: '',
      expected_salary: '',
      expected_benefits: '',
      skills: '',
      willing_medical: 0,
      willing_psychology: 0,
      willing_relocate: 0,
      willing_trial: 0,
      willing_transfer: 0,
      willing_rules: 0,
      computer_skill: 0,
      computer_programs: '',
      typing_level: '',
      driving_ability: '',
      vehicle_knowledge: 0
    },
    
    // Pendidikan Formal
    education: [],
    
    // Kursus
    courses: [],
    
    // Umum & Kesehatan
    generalHealth: {
      willing_original_certificate: 0,
      allow_contact_employer: 0,
      ever_fired: 0,
      decision_advice_source: '',
      decision_advice_note: '',
      personal_advice_source: '',
      personal_advice_note: '',
      reaction_to_criticism: '',
      common_illness: '',
      serious_illness: 0,
      serious_illness_desc: '',
      eye_disease: 0,
      lung_disease: 0,
      ear_disease: 0,
      diabetes: 0,
      hypertension: 0,
      heart_disease: 0,
      last_checkup: '',
      strength: '',
      weakness: ''
    },
    
    // Lain-lain
    others: {
      emergency_relations: [],
      former_relations: [],
      guarantors: []
    },
    
    applicationStatus: 'Pending',
    applicationDate: dayjs().format('YYYY-MM-DD')
  });

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const steps = [
    'Data Pribadi Dasar',
    'Data Pribadi Lanjutan', 
    'Pendidikan & Kursus',
    'Riwayat Pekerjaan',
    'Keluarga & Aktivitas',
    'Lamaran & Posisi',
    'Keterampilan',
    'Kesehatan & Umum',
    'Kontak & Penjamin'
  ];

  const handleInputChange = (section, field, value) => {
    setCandidateFormData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
    
    // Clear error when user starts typing
    if (errors[`${section}.${field}`]) {
      setErrors(prev => ({
        ...prev,
        [`${section}.${field}`]: ''
      }));
    }
  };

  const handleArrayInputChange = (section, field, index, subField, value) => {
    setCandidateFormData(prev => {
      if (section === '') {
        // For root level arrays like education, courses
        return {
          ...prev,
          [field]: prev[field].map((item, i) => 
            i === index ? { ...item, [subField]: value } : item
          )
        };
      } else {
        // For nested arrays like family.members, others.emergency_relations
        return {
          ...prev,
          [section]: {
            ...prev[section],
            [field]: prev[section][field].map((item, i) => 
              i === index ? { ...item, [subField]: value } : item
            )
          }
        };
      }
    });
  };

  const addArrayItem = (section, field, newItem) => {
    setCandidateFormData(prev => {
      if (section === '') {
        // For root level arrays like education, courses
        return {
          ...prev,
          [field]: [...(prev[field] || []), newItem]
        };
      } else {
        // For nested arrays like family.members, others.emergency_relations
        return {
          ...prev,
          [section]: {
            ...prev[section],
            [field]: [...(prev[section][field] || []), newItem]
          }
        };
      }
    });
  };

  const removeArrayItem = (section, field, index) => {
    setCandidateFormData(prev => {
      if (section === '') {
        // For root level arrays like education, courses
        return {
          ...prev,
          [field]: prev[field].filter((_, i) => i !== index)
        };
      } else {
        // For nested arrays like family.members, others.emergency_relations
        return {
          ...prev,
          [section]: {
            ...prev[section],
            [field]: prev[section][field].filter((_, i) => i !== index)
          }
        };
      }
    });
  };

  const validateStep = (step) => {
    const newErrors = {};
    
    switch (step) {
      case 0: // Data Pribadi Dasar
        if (!candidateFormData.personalData.full_name) {
          newErrors['personalData.full_name'] = 'Nama lengkap wajib diisi';
        }
        if (!candidateFormData.applicationSkills.desired_position) {
          newErrors['applicationSkills.desired_position'] = 'Jabatan yang diinginkan wajib diisi';
        }
        break;
      case 1: // Data Pribadi Lanjutan
        // Optional fields, no validation required
        break;
      case 2: // Pendidikan & Kursus
        // Optional - user can skip if fresh graduate or no courses
        break;
      case 3: // Riwayat Pekerjaan
        // Optional - user might be fresh graduate
        break;
      case 4: // Keluarga & Aktivitas
        // Optional fields
        break;
      case 5: // Lamaran & Posisi
        // Posisi sudah dipindah ke step 0, sisanya optional
        break;
      case 6: // Keterampilan
        // Optional fields
        break;
      case 7: // Kesehatan & Umum
        // Optional fields
        break;
      case 8: // Kontak & Penjamin
        // Optional fields
        break;
      default:
        break;
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(activeStep)) {
      setActiveStep((prevStep) => prevStep + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const handleSubmit = async () => {
    if (!validateStep(activeStep)) {
      return;
    }

    setLoading(true);
    
    try {
      // Prepare data for API
      const apiData = {
        ...candidateFormData,
        applicationStatus: 'Pending'  // Auto-set status to Pending for new candidates
      };
      
      console.log('=== CANDIDATE SUBMISSION DEBUG ===');
      console.log('Full candidate form data:', candidateFormData);
      console.log('API Data to be sent:', apiData);
      console.log('JSON Size:', JSON.stringify(apiData).length, 'characters');
      console.log('Personal Data:', apiData.personalData);
      console.log('History:', apiData.history);
      console.log('Work History:', apiData.workHistory);
      console.log('Social Activities:', apiData.socialActivities);
      console.log('Family:', apiData.family);
      console.log('Application Skills:', apiData.applicationSkills);
      console.log('Education:', apiData.education);
      console.log('Courses:', apiData.courses);
      console.log('General Health:', apiData.generalHealth);
      console.log('Others:', apiData.others);
      
      // Detailed field count
      console.log('=== FIELD COUNT ===');
      console.log('personalData fields:', Object.keys(apiData.personalData).length);
      console.log('history fields:', Object.keys(apiData.history).length);
      console.log('workHistory items:', apiData.workHistory.length);
      console.log('socialActivities items:', apiData.socialActivities.length);
      console.log('family.members items:', apiData.family.members?.length || 0);
      console.log('applicationSkills fields:', Object.keys(apiData.applicationSkills).length);
      console.log('education items:', apiData.education.length);
      console.log('courses items:', apiData.courses.length);
      console.log('generalHealth fields:', Object.keys(apiData.generalHealth).length);
      console.log('others.emergency_relations items:', apiData.others.emergency_relations?.length || 0);
      console.log('others.former_relations items:', apiData.others.former_relations?.length || 0);
      console.log('others.guarantors items:', apiData.others.guarantors?.length || 0);
      
      // Call API
      const response = await fetch('https://apidashboard.eurekagroup.id/candidates', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}` // Add token if needed
        },
        body: JSON.stringify(apiData)
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const result = await response.json();
      console.log('API Response:', result);
      
      Swal.fire({
        ...SwalConfig.success,
        title: 'Berhasil!',
        text: 'Data kandidat berhasil ditambahkan'
      }).then(() => {
        navigate('/kontak/candidate');
      });
      
    } catch (error) {
      console.error('Error adding candidate:', error);
      Swal.fire({
        ...SwalConfig.error,
        title: 'Error!',
        text: `Gagal menambahkan data kandidat: ${error.message}`
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSimpleSubmit = async () => {
    // Validate basic personal data
    // Wajib: Nama lengkap & Jabatan yang diinginkan
    if (!candidateFormData.personalData.full_name || !candidateFormData.applicationSkills?.desired_position) {
      Swal.fire({ ...SwalConfig.error, title: 'Validation', text: 'Nama lengkap dan jabatan yang diinginkan wajib diisi.' });
      return;
    }

    setLoading(true);
    try {
      const apiData = {
        personalData: {
          full_name: candidateFormData.personalData.full_name,
          gender: candidateFormData.personalData.gender,
          phone: candidateFormData.personalData.phone || '',
          birth_date: candidateFormData.personalData.birth_date || ''
        },
        applicationSkills: {
          desired_position: candidateFormData.applicationSkills?.desired_position || '',
          expected_salary: candidateFormData.applicationSkills?.expected_salary || '0'
        },
        applicationStatus: 'Pending',
        applicationDate: dayjs().format('YYYY-MM-DD')
      };

      const response = await fetch('https://apidashboard.eurekagroup.id/candidates', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(apiData)
      });

      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const result = await response.json();

      const createdId = result.data?._id || result._id || result.id || result.data?.id;
      // Build a public continuation URL (unique by id)
      const publicUrl = `${window.location.origin}/public/candidate/continue/${createdId}`;

      Swal.fire({
        ...SwalConfig.success,
        title: 'Berhasil!',
        html: `Kandidat dibuat. <br/> Lanjutkan pengisian publik: <a href="${publicUrl}" target="_blank" rel="noopener noreferrer">${publicUrl}</a>`
      }).then(() => navigate('/kontak/candidate'));
    } catch (err) {
      console.error('Error simple creating candidate', err);
      Swal.fire({ ...SwalConfig.error, title: 'Error', text: `Gagal membuat kandidat: ${err.message}` });
    } finally {
      setLoading(false);
    }
  };

  const renderPersonalDataStep = () => (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PersonIcon color="primary" />
          Data Pribadi Dasar
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Isi data pribadi dasar kandidat. Field bertanda * wajib diisi.
        </Typography>
      </Grid>
      
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          required
          label="Nama Lengkap"
          value={candidateFormData.personalData.full_name}
          onChange={(e) => handleInputChange('personalData', 'full_name', e.target.value)}
          error={!!errors['personalData.full_name']}
          helperText={errors['personalData.full_name'] || 'Nama lengkap sesuai KTP'}
        />
      </Grid>
      
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          required
          label="Jabatan yang Diinginkan"
          value={candidateFormData.applicationSkills.desired_position}
          onChange={(e) => handleInputChange('applicationSkills', 'desired_position', e.target.value)}
          error={!!errors['applicationSkills.desired_position']}
          helperText={errors['applicationSkills.desired_position'] || 'Posisi yang Anda lamar'}
        />
      </Grid>
      
      <Grid item xs={12} md={6}>
        <FormControl fullWidth>
          <InputLabel>Jenis Kelamin</InputLabel>
          <Select
            value={candidateFormData.personalData.gender}
            onChange={(e) => handleInputChange('personalData', 'gender', e.target.value)}
            label="Jenis Kelamin"
          >
            <MenuItem value="Male">Laki-laki</MenuItem>
            <MenuItem value="Female">Perempuan</MenuItem>
          </Select>
        </FormControl>
      </Grid>
      
      <Grid item xs={12} md={4}>
        <TextField
          fullWidth
          label="Tempat Lahir"
          value={candidateFormData.personalData.birth_place}
          onChange={(e) => handleInputChange('personalData', 'birth_place', e.target.value)}
        />
      </Grid>
      
      <Grid item xs={12} md={4}>
        <TextField
          fullWidth
          label="Tanggal Lahir"
          type="date"
          value={candidateFormData.personalData.birth_date}
          onChange={(e) => handleInputChange('personalData', 'birth_date', e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
      </Grid>
      
      <Grid item xs={12} md={4}>
        <TextField
          fullWidth
          label="Usia"
          type="number"
          value={candidateFormData.personalData.age}
          onChange={(e) => handleInputChange('personalData', 'age', e.target.value)}
          helperText="Akan otomatis dihitung dari tanggal lahir"
        />
      </Grid>
      
      <Grid item xs={12} md={3}>
        <TextField
          fullWidth
          label="Agama"
          value={candidateFormData.personalData.religion}
          onChange={(e) => handleInputChange('personalData', 'religion', e.target.value)}
        />
      </Grid>
      
      <Grid item xs={12} md={3}>
        <TextField
          fullWidth
          label="Suku"
          value={candidateFormData.personalData.ethnicity}
          onChange={(e) => handleInputChange('personalData', 'ethnicity', e.target.value)}
        />
      </Grid>
      
      <Grid item xs={12} md={3}>
        <TextField
          fullWidth
          label="Tinggi Badan (cm)"
          type="number"
          value={candidateFormData.personalData.height_cm}
          onChange={(e) => handleInputChange('personalData', 'height_cm', e.target.value)}
        />
      </Grid>
      
      <Grid item xs={12} md={3}>
        <TextField
          fullWidth
          label="Berat Badan (kg)"
          type="number"
          value={candidateFormData.personalData.weight_kg}
          onChange={(e) => handleInputChange('personalData', 'weight_kg', e.target.value)}
        />
      </Grid>
      
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Nomor Telepon"
          value={candidateFormData.personalData.phone}
          onChange={(e) => handleInputChange('personalData', 'phone', e.target.value)}
          helperText="Nomor yang dapat dihubungi"
        />
      </Grid>
      
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="NIK"
          value={candidateFormData.personalData.id_number}
          onChange={(e) => handleInputChange('personalData', 'id_number', e.target.value)}
          helperText="Nomor Induk Kependudukan (KTP)"
        />
      </Grid>
      
      <Grid item xs={12}>
        <TextField
          fullWidth
          label="Alamat Lengkap"
          multiline
          rows={3}
          value={candidateFormData.personalData.address}
          onChange={(e) => handleInputChange('personalData', 'address', e.target.value)}
          helperText="Alamat lengkap sesuai KTP"
        />
      </Grid>
    </Grid>
  );

  const renderPersonalDataAdvancedStep = () => (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PersonIcon color="primary" />
          Data Pribadi Lanjutan
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Lengkapi data pribadi tambahan dan prestasi kandidat.
        </Typography>
      </Grid>
      
      {/* Daerah KTP */}
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Daerah Sesuai KTP"
          value={candidateFormData.personalData.id_region}
          onChange={(e) => handleInputChange('personalData', 'id_region', e.target.value)}
          helperText="Contoh: DKI Jakarta, Jawa Barat"
        />
      </Grid>
      
      {/* Status Tinggal */}
      <Grid item xs={12} md={6}>
        <FormControl component="fieldset">
          <FormLabel component="legend">Status Tinggal</FormLabel>
          <RadioGroup
            row
            value={candidateFormData.personalData.living_status}
            onChange={(e) => handleInputChange('personalData', 'living_status', e.target.value)}
          >
            <FormControlLabel value="Parents" control={<Radio />} label="Orang Tua" />
            <FormControlLabel value="Own" control={<Radio />} label="Sendiri" />
            <FormControlLabel value="Rent" control={<Radio />} label="Kost/Kontrak" />
          </RadioGroup>
        </FormControl>
      </Grid>
      
      {/* Jenis Kendaraan */}
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Jenis Kendaraan"
          value={candidateFormData.personalData.vehicle_type}
          onChange={(e) => handleInputChange('personalData', 'vehicle_type', e.target.value)}
          helperText="Contoh: Motor, Mobil, Sepeda"
        />
      </Grid>
      
      {/* Kepemilikan Kendaraan */}
      <Grid item xs={12} md={6}>
        <FormControl component="fieldset">
          <FormLabel component="legend">Kepemilikan Kendaraan</FormLabel>
          <RadioGroup
            row
            value={candidateFormData.personalData.vehicle_ownership}
            onChange={(e) => handleInputChange('personalData', 'vehicle_ownership', e.target.value)}
          >
            <FormControlLabel value="Own" control={<Radio />} label="Milik Sendiri" />
            <FormControlLabel value="Public" control={<Radio />} label="Kendaraan Umum" />
            <FormControlLabel value="Family" control={<Radio />} label="Milik Keluarga" />
          </RadioGroup>
        </FormControl>
      </Grid>
      
      <Grid item xs={12}>
        <Divider sx={{ my: 2 }}>
          <Chip label="Prestasi & Pencapaian" />
        </Divider>
      </Grid>
      
      {/* Prestasi Terbaik */}
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Prestasi Terbaik"
          value={candidateFormData.personalData.best_achievement}
          onChange={(e) => handleInputChange('personalData', 'best_achievement', e.target.value)}
          helperText="Contoh: Juara 1 Lomba Matematika"
        />
      </Grid>
      
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Keterangan Prestasi Terbaik"
          value={candidateFormData.personalData.best_achievement_desc}
          onChange={(e) => handleInputChange('personalData', 'best_achievement_desc', e.target.value)}
          helperText="Jelaskan detail prestasi"
        />
      </Grid>
      
      {/* Prestasi Terendah */}
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Prestasi Terendah"
          value={candidateFormData.personalData.worst_achievement}
          onChange={(e) => handleInputChange('personalData', 'worst_achievement', e.target.value)}
          helperText="Contoh: Nilai seni rupa terendah"
        />
      </Grid>
      
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Keterangan Prestasi Terendah"
          value={candidateFormData.personalData.worst_achievement_desc}
          onChange={(e) => handleInputChange('personalData', 'worst_achievement_desc', e.target.value)}
          helperText="Jelaskan detail"
        />
      </Grid>
      
      <Grid item xs={12}>
        <Divider sx={{ my: 2 }}>
          <Chip label="Bahasa & Pendidikan" />
        </Divider>
      </Grid>
      
      {/* Bahasa */}
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Bahasa yang Dikuasai (Aktif)"
          value={candidateFormData.personalData.language_active}
          onChange={(e) => handleInputChange('personalData', 'language_active', e.target.value)}
          helperText="Contoh: Indonesia, Inggris"
        />
      </Grid>
      
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Bahasa yang Dikuasai (Pasif)"
          value={candidateFormData.personalData.language_passive}
          onChange={(e) => handleInputChange('personalData', 'language_passive', e.target.value)}
          helperText="Contoh: Jepang (dasar)"
        />
      </Grid>
      
      {/* Beasiswa */}
      <Grid item xs={12} md={4}>
        <TextField
          fullWidth
          label="Beasiswa yang Pernah Diterima"
          value={candidateFormData.personalData.scholarship}
          onChange={(e) => handleInputChange('personalData', 'scholarship', e.target.value)}
          helperText="Nama beasiswa"
        />
      </Grid>
      
      <Grid item xs={12} md={4}>
        <TextField
          fullWidth
          label="Durasi Beasiswa"
          value={candidateFormData.personalData.scholarship_duration}
          onChange={(e) => handleInputChange('personalData', 'scholarship_duration', e.target.value)}
          helperText="Contoh: 2 tahun"
        />
      </Grid>
      
      <Grid item xs={12} md={4}>
        <TextField
          fullWidth
          label="Keterangan Beasiswa"
          value={candidateFormData.personalData.scholarship_desc}
          onChange={(e) => handleInputChange('personalData', 'scholarship_desc', e.target.value)}
          helperText="Detail beasiswa"
        />
      </Grid>
      
      {/* Sponsor Pendidikan */}
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Siapa yang Membiayai Pendidikan?"
          value={candidateFormData.personalData.education_sponsor}
          onChange={(e) => handleInputChange('personalData', 'education_sponsor', e.target.value)}
          helperText="Contoh: Orang tua, Beasiswa"
        />
      </Grid>
      
      {/* Publikasi */}
      <Grid item xs={12}>
        <TextField
          fullWidth
          label="Karya Ilmiah / Skripsi / Artikel / Buku"
          value={candidateFormData.personalData.publication}
          onChange={(e) => handleInputChange('personalData', 'publication', e.target.value)}
          helperText="Contoh: Skripsi: Optimasi Rute Kurir"
        />
      </Grid>
      
      <Grid item xs={12}>
        <Divider sx={{ my: 2 }}>
          <Chip label="Nilai Akademik" />
        </Divider>
      </Grid>
      
      {/* Nilai SMA */}
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Jumlah Nilai Rata-Rata SMA/SMK"
          type="number"
          value={candidateFormData.personalData.highschool_avg_score}
          onChange={(e) => handleInputChange('personalData', 'highschool_avg_score', e.target.value)}
          helperText="Contoh: 82.50"
        />
      </Grid>
      
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Jumlah Mata Pelajaran SMA/SMK"
          type="number"
          value={candidateFormData.personalData.highschool_subject_count}
          onChange={(e) => handleInputChange('personalData', 'highschool_subject_count', e.target.value)}
          helperText="Contoh: 12"
        />
      </Grid>
      
      {/* IPK */}
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="IPK / Indeks Prestasi"
          type="number"
          value={candidateFormData.personalData.gpa}
          onChange={(e) => handleInputChange('personalData', 'gpa', e.target.value)}
          helperText="Contoh: 3.60"
        />
      </Grid>
      
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="IPK Maksimum"
          type="number"
          value={candidateFormData.personalData.gpa_max}
          onChange={(e) => handleInputChange('personalData', 'gpa_max', e.target.value)}
          helperText="Contoh: 4.00"
        />
      </Grid>
    </Grid>
  );

  const renderEducationCoursesStep = () => (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SchoolIcon color="primary" />
          Pendidikan & Kursus
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Tambahkan riwayat pendidikan formal dan kursus yang pernah diikuti.
        </Typography>
      </Grid>
      
      {/* Pendidikan Formal */}
      <Grid item xs={12}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="subtitle1" fontWeight={600}>
            Riwayat Pendidikan Formal
          </Typography>
          <Button
            startIcon={<AddIcon />}
            onClick={() => addArrayItem('', 'education', {
              jenjang: '',
              nama_sekolah: '',
              tempat: '',
              tahun_lulus: '',
              rata_rata_nilai: '',
              jurusan: ''
            })}
            variant="outlined"
            size="small"
          >
            Tambah Pendidikan
          </Button>
        </Box>
        
        {candidateFormData.education.length === 0 && (
          <Paper sx={{ p: 3, textAlign: 'center', bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'action.hover' }}>
            <Typography variant="body2" color="text.secondary">
              Belum ada data pendidikan. Klik "Tambah Pendidikan" untuk menambah.
            </Typography>
          </Paper>
        )}
        
        {candidateFormData.education.map((edu, index) => (
          <Paper key={index} sx={{ p: 2, mb: 2, border: '1px solid', borderColor: 'grey.300' }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Chip label={`Pendidikan ${index + 1}`} color="primary" size="small" />
                  <IconButton
                    onClick={() => removeArrayItem('', 'education', index)}
                    color="error"
                    size="small"
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Grid>
              
              <Grid item xs={12} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Jenjang</InputLabel>
                  <Select
                    value={edu.jenjang}
                    onChange={(e) => handleArrayInputChange('', 'education', index, 'jenjang', e.target.value)}
                    label="Jenjang"
                  >
                    <MenuItem value="SD">SD</MenuItem>
                    <MenuItem value="SMP">SMP</MenuItem>
                    <MenuItem value="SMA">SMA</MenuItem>
                    <MenuItem value="SMK">SMK</MenuItem>
                    <MenuItem value="D3">D3</MenuItem>
                    <MenuItem value="S1">S1</MenuItem>
                    <MenuItem value="S2">S2</MenuItem>
                    <MenuItem value="S3">S3</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Nama Sekolah / Universitas"
                  value={edu.nama_sekolah}
                  onChange={(e) => handleArrayInputChange('', 'education', index, 'nama_sekolah', e.target.value)}
                />
              </Grid>
              
              <Grid item xs={12} md={2}>
                <TextField
                  fullWidth
                  size="small"
                  label="Tempat"
                  value={edu.tempat}
                  onChange={(e) => handleArrayInputChange('', 'education', index, 'tempat', e.target.value)}
                />
              </Grid>
              
              <Grid item xs={12} md={1.5}>
                <TextField
                  fullWidth
                  size="small"
                  label="Tahun Lulus"
                  value={edu.tahun_lulus}
                  onChange={(e) => handleArrayInputChange('', 'education', index, 'tahun_lulus', e.target.value)}
                />
              </Grid>
              
              <Grid item xs={12} md={1.5}>
                <TextField
                  fullWidth
                  size="small"
                  label="Nilai"
                  value={edu.rata_rata_nilai}
                  onChange={(e) => handleArrayInputChange('', 'education', index, 'rata_rata_nilai', e.target.value)}
                />
              </Grid>
              
              <Grid item xs={12} md={1}>
                <TextField
                  fullWidth
                  size="small"
                  label="Jurusan"
                  value={edu.jurusan}
                  onChange={(e) => handleArrayInputChange('', 'education', index, 'jurusan', e.target.value)}
                />
              </Grid>
            </Grid>
          </Paper>
        ))}
        
        {errors['education'] && (
          <Typography variant="caption" color="error" sx={{ ml: 2 }}>
            {errors['education']}
          </Typography>
        )}
      </Grid>
      
      <Grid item xs={12}>
        <Divider sx={{ my: 2 }} />
      </Grid>
      
      {/* Kursus */}
      <Grid item xs={12}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="subtitle1" fontWeight={600}>
            Riwayat Kursus & Pelatihan
          </Typography>
          <Button
            startIcon={<AddIcon />}
            onClick={() => addArrayItem('', 'courses', {
              instruktur_lembaga: '',
              tempat: '',
              lama_pendidikan: '',
              yang_membiayai: '',
              keterangan: ''
            })}
            variant="outlined"
            size="small"
          >
            Tambah Kursus
          </Button>
        </Box>
        
        {candidateFormData.courses.length === 0 && (
          <Paper sx={{ p: 3, textAlign: 'center', bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'action.hover' }}>
            <Typography variant="body2" color="text.secondary">
              Belum ada data kursus. Klik "Tambah Kursus" untuk menambah.
            </Typography>
          </Paper>
        )}
        
        {candidateFormData.courses.map((course, index) => (
          <Paper key={index} sx={{ p: 2, mb: 2, border: '1px solid', borderColor: 'grey.300' }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Chip label={`Kursus ${index + 1}`} color="secondary" size="small" />
                  <IconButton
                    onClick={() => removeArrayItem('', 'courses', index)}
                    color="error"
                    size="small"
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Grid>
              
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="Instruktur / Lembaga"
                  value={course.instruktur_lembaga}
                  onChange={(e) => handleArrayInputChange('', 'courses', index, 'instruktur_lembaga', e.target.value)}
                />
              </Grid>
              
              <Grid item xs={12} md={2}>
                <TextField
                  fullWidth
                  size="small"
                  label="Tempat"
                  value={course.tempat}
                  onChange={(e) => handleArrayInputChange('', 'courses', index, 'tempat', e.target.value)}
                />
              </Grid>
              
              <Grid item xs={12} md={2}>
                <TextField
                  fullWidth
                  size="small"
                  label="Lama Pendidikan"
                  value={course.lama_pendidikan}
                  onChange={(e) => handleArrayInputChange('', 'courses', index, 'lama_pendidikan', e.target.value)}
                  helperText="Contoh: 3 bulan"
                />
              </Grid>
              
              <Grid item xs={12} md={2}>
                <TextField
                  fullWidth
                  size="small"
                  label="Yang Membiayai"
                  value={course.yang_membiayai}
                  onChange={(e) => handleArrayInputChange('', 'courses', index, 'yang_membiayai', e.target.value)}
                />
              </Grid>
              
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="Keterangan"
                  value={course.keterangan}
                  onChange={(e) => handleArrayInputChange('', 'courses', index, 'keterangan', e.target.value)}
                />
              </Grid>
            </Grid>
          </Paper>
        ))}
      </Grid>
    </Grid>
  );

  const renderWorkHistoryStep = () => (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WorkIcon color="primary" />
          Riwayat Pekerjaan & Aktivitas
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Tambahkan riwayat pekerjaan, hobi, dan aktivitas sehari-hari.
        </Typography>
      </Grid>
      
      {/* Riwayat Pekerjaan */}
      <Grid item xs={12}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="subtitle1" fontWeight={600}>
            Riwayat Pekerjaan
          </Typography>
          <Button
            startIcon={<AddIcon />}
            onClick={() => addArrayItem('', 'workHistory', {
              masuk: '',
              keluar: '',
              jabatan: '',
              perusahaan: '',
              alamat_telp: '',
              keterangan: ''
            })}
            variant="outlined"
            size="small"
          >
            Tambah Pekerjaan
          </Button>
        </Box>
        
        {candidateFormData.workHistory.length === 0 && (
          <Paper sx={{ p: 3, textAlign: 'center', bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'action.hover' }}>
            <Typography variant="body2" color="text.secondary">
              Belum ada riwayat pekerjaan. Klik "Tambah Pekerjaan" untuk menambah.
            </Typography>
          </Paper>
        )}
        
        {candidateFormData.workHistory.map((work, index) => (
          <Paper key={index} sx={{ p: 2, mb: 2, border: '1px solid', borderColor: 'grey.300' }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Chip label={`Pekerjaan ${index + 1}`} color="info" size="small" />
                  <IconButton
                    onClick={() => removeArrayItem('', 'workHistory', index)}
                    color="error"
                    size="small"
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Grid>
              
              <Grid item xs={12} md={2}>
                <TextField
                  fullWidth
                  size="small"
                  label="Tahun Masuk"
                  type="date"
                  value={work.masuk}
                  onChange={(e) => handleArrayInputChange('', 'workHistory', index, 'masuk', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              
              <Grid item xs={12} md={2}>
                <TextField
                  fullWidth
                  size="small"
                  label="Tahun Keluar"
                  type="date"
                  value={work.keluar}
                  onChange={(e) => handleArrayInputChange('', 'workHistory', index, 'keluar', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="Jabatan"
                  value={work.jabatan}
                  onChange={(e) => handleArrayInputChange('', 'workHistory', index, 'jabatan', e.target.value)}
                />
              </Grid>
              
              <Grid item xs={12} md={5}>
                <TextField
                  fullWidth
                  size="small"
                  label="Nama Perusahaan"
                  value={work.perusahaan}
                  onChange={(e) => handleArrayInputChange('', 'workHistory', index, 'perusahaan', e.target.value)}
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Alamat & Telepon"
                  value={work.alamat_telp}
                  onChange={(e) => handleArrayInputChange('', 'workHistory', index, 'alamat_telp', e.target.value)}
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Keterangan"
                  value={work.keterangan}
                  onChange={(e) => handleArrayInputChange('', 'workHistory', index, 'keterangan', e.target.value)}
                />
              </Grid>
            </Grid>
          </Paper>
        ))}
      </Grid>
      
      <Grid item xs={12}>
        <Divider sx={{ my: 2 }}>
          <Chip label="Detail Pekerjaan Terakhir" />
        </Divider>
      </Grid>
      
      {/* Detail Pekerjaan Terakhir */}
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Alasan Keluar dari Pekerjaan Terakhir"
          multiline
          rows={3}
          value={candidateFormData.history.last_job_reason_leave}
          onChange={(e) => handleInputChange('history', 'last_job_reason_leave', e.target.value)}
        />
      </Grid>
      
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Gaji / Tunjangan Pekerjaan Terakhir"
          value={candidateFormData.history.last_job_salary}
          onChange={(e) => handleInputChange('history', 'last_job_salary', e.target.value)}
          helperText="Contoh: Rp 8.000.000"
        />
      </Grid>
      
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Pekerjaan yang Paling Disukai"
          value={candidateFormData.history.job_most_liked}
          onChange={(e) => handleInputChange('history', 'job_most_liked', e.target.value)}
        />
      </Grid>
      
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Pekerjaan yang Kurang Disukai"
          value={candidateFormData.history.job_least_liked}
          onChange={(e) => handleInputChange('history', 'job_least_liked', e.target.value)}
        />
      </Grid>
      
      <Grid item xs={12}>
        <Divider sx={{ my: 2 }}>
          <Chip label="Hobi & Waktu Luang" icon={<SportsIcon />} />
        </Divider>
      </Grid>
      
      {/* Hobi & Aktivitas */}
      <Grid item xs={12} md={4}>
        <TextField
          fullWidth
          label="Hobi"
          value={candidateFormData.history.hobby}
          onChange={(e) => handleInputChange('history', 'hobby', e.target.value)}
          helperText="Contoh: Fotografi, bersepeda"
        />
      </Grid>
      
      <Grid item xs={12} md={4}>
        <FormControl component="fieldset">
          <FormLabel component="legend">Apakah Anda Berolahraga?</FormLabel>
          <FormControlLabel
            control={
              <Switch
                checked={!!candidateFormData.history.sport}
                onChange={(e) => handleInputChange('history', 'sport', e.target.checked ? 1 : 0)}
              />
            }
            label={candidateFormData.history.sport ? 'Ya' : 'Tidak'}
          />
        </FormControl>
      </Grid>
      
      <Grid item xs={12} md={4}>
        <TextField
          fullWidth
          label="Keterangan Olahraga"
          value={candidateFormData.history.sport_desc}
          onChange={(e) => handleInputChange('history', 'sport_desc', e.target.value)}
          disabled={!candidateFormData.history.sport}
          helperText="Contoh: Futsal mingguan"
        />
      </Grid>
      
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Waktu Luang Diisi Dengan"
          value={candidateFormData.history.free_time}
          onChange={(e) => handleInputChange('history', 'free_time', e.target.value)}
          helperText="Contoh: Belajar online"
        />
      </Grid>
      
      <Grid item xs={12}>
        <Divider sx={{ my: 2 }}>
          <Chip label="Kebiasaan Membaca" icon={<BookIcon />} />
        </Divider>
      </Grid>
      
      {/* Membaca */}
      <Grid item xs={12} md={4}>
        <FormControl fullWidth>
          <InputLabel>Anda Membaca Per Hari</InputLabel>
          <Select
            value={candidateFormData.history.reading_hours}
            onChange={(e) => handleInputChange('history', 'reading_hours', e.target.value)}
            label="Anda Membaca Per Hari"
          >
            <MenuItem value="<1h">Kurang dari 1 jam</MenuItem>
            <MenuItem value="1-3h">1 - 3 jam</MenuItem>
            <MenuItem value=">3h">Lebih dari 3 jam</MenuItem>
          </Select>
        </FormControl>
      </Grid>
      
      <Grid item xs={12} md={8}>
        <TextField
          fullWidth
          label="Topik Bacaan yang Digemari"
          value={candidateFormData.history.reading_topics}
          onChange={(e) => handleInputChange('history', 'reading_topics', e.target.value)}
          helperText="Contoh: Teknologi, Logistik"
        />
      </Grid>
      
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Berlangganan Surat Kabar"
          value={candidateFormData.history.newspaper}
          onChange={(e) => handleInputChange('history', 'newspaper', e.target.value)}
          helperText="Contoh: Kompas"
        />
      </Grid>
      
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Berlangganan Majalah"
          value={candidateFormData.history.magazine}
          onChange={(e) => handleInputChange('history', 'magazine', e.target.value)}
          helperText="Contoh: SWA"
        />
      </Grid>
    </Grid>
  );

  const renderFamilyActivitiesStep = () => (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <GroupIcon color="primary" />
          Keluarga & Aktivitas Sosial
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Tambahkan informasi keluarga dan aktivitas sosial.
        </Typography>
      </Grid>
      
      {/* Status Pernikahan */}
      <Grid item xs={12} md={4}>
        <FormControl fullWidth>
          <InputLabel>Status Pernikahan</InputLabel>
          <Select
            value={candidateFormData.family.marital_status}
            onChange={(e) => handleInputChange('family', 'marital_status', e.target.value)}
            label="Status Pernikahan"
          >
            <MenuItem value="Single">Belum Menikah</MenuItem>
            <MenuItem value="Married">Menikah</MenuItem>
            <MenuItem value="Divorced">Cerai</MenuItem>
            <MenuItem value="Widowed">Janda/Duda</MenuItem>
          </Select>
        </FormControl>
      </Grid>
      
      <Grid item xs={12} md={4}>
        <TextField
          fullWidth
          label="Tanggal Menikah"
          type="date"
          value={candidateFormData.family.marriage_date}
          onChange={(e) => handleInputChange('family', 'marriage_date', e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
      </Grid>
      
      <Grid item xs={12} md={4}>
        <TextField
          fullWidth
          label="Pernikahan Ke-"
          type="number"
          value={candidateFormData.family.marriage_count}
          onChange={(e) => handleInputChange('family', 'marriage_count', e.target.value)}
        />
      </Grid>
      
      {/* Anggota Keluarga */}
      <Grid item xs={12}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, mt: 2 }}>
          <Typography variant="subtitle1" fontWeight={600}>
            Anggota Keluarga
        </Typography>
        <Button
          startIcon={<AddIcon />}
            onClick={() => addArrayItem('family', 'members', {
              jenis: '',
              nama: '',
              gender: '',
              usia: '',
              pendidikan_terakhir: '',
              pekerjaan: '',
              jabatan: '',
              perusahaan: '',
              keterangan: ''
          })}
          variant="outlined"
            size="small"
        >
            Tambah Anggota Keluarga
        </Button>
        </Box>
        
        {candidateFormData.family.members.length === 0 && (
          <Paper sx={{ p: 3, textAlign: 'center', bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'action.hover' }}>
            <Typography variant="body2" color="text.secondary">
              Belum ada anggota keluarga. Klik "Tambah Anggota Keluarga" untuk menambah.
            </Typography>
          </Paper>
        )}
        
        {candidateFormData.family.members.map((member, index) => (
          <Paper key={index} sx={{ p: 2, mb: 2, border: '1px solid', borderColor: 'grey.300' }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Chip label={`Anggota ${index + 1}`} color="success" size="small" />
                  <IconButton
                    onClick={() => removeArrayItem('family', 'members', index)}
                    color="error"
                    size="small"
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Grid>
              
              <Grid item xs={12} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Jenis</InputLabel>
                  <Select
                    value={member.jenis}
                    onChange={(e) => handleArrayInputChange('family', 'members', index, 'jenis', e.target.value)}
                    label="Jenis"
                  >
                    <MenuItem value="Ayah">Ayah</MenuItem>
                    <MenuItem value="Ibu">Ibu</MenuItem>
                    <MenuItem value="Kakak">Kakak</MenuItem>
                    <MenuItem value="Adik">Adik</MenuItem>
                    <MenuItem value="Suami">Suami</MenuItem>
                    <MenuItem value="Istri">Istri</MenuItem>
                    <MenuItem value="Anak">Anak</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} md={2.5}>
                <TextField
                  fullWidth
                  size="small"
                  label="Nama"
                  value={member.nama}
                  onChange={(e) => handleArrayInputChange('family', 'members', index, 'nama', e.target.value)}
                />
              </Grid>
              
              <Grid item xs={12} md={1}>
                <FormControl fullWidth size="small">
                  <InputLabel>L/P</InputLabel>
                  <Select
                    value={member.gender}
                    onChange={(e) => handleArrayInputChange('family', 'members', index, 'gender', e.target.value)}
                    label="L/P"
                  >
                    <MenuItem value="L">L</MenuItem>
                    <MenuItem value="P">P</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} md={1}>
                <TextField
                  fullWidth
                  size="small"
                  label="Usia"
                  type="number"
                  value={member.usia}
                  onChange={(e) => handleArrayInputChange('family', 'members', index, 'usia', e.target.value)}
                />
              </Grid>
              
              <Grid item xs={12} md={1.5}>
                <TextField
                  fullWidth
                  size="small"
                  label="Pendidikan"
                  value={member.pendidikan_terakhir}
                  onChange={(e) => handleArrayInputChange('family', 'members', index, 'pendidikan_terakhir', e.target.value)}
                />
              </Grid>
              
              <Grid item xs={12} md={2}>
                <TextField
                  fullWidth
                  size="small"
                  label="Pekerjaan"
                  value={member.pekerjaan}
                  onChange={(e) => handleArrayInputChange('family', 'members', index, 'pekerjaan', e.target.value)}
                />
              </Grid>
              
              <Grid item xs={12} md={2}>
                <TextField
                  fullWidth
                  size="small"
                  label="Perusahaan"
                  value={member.perusahaan}
                  onChange={(e) => handleArrayInputChange('family', 'members', index, 'perusahaan', e.target.value)}
                />
              </Grid>
            </Grid>
          </Paper>
        ))}
      </Grid>
      
      <Grid item xs={12}>
        <Divider sx={{ my: 2 }} />
      </Grid>
      
      {/* Aktivitas Sosial */}
      <Grid item xs={12}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="subtitle1" fontWeight={600}>
            Aktivitas Sosial & Organisasi
          </Typography>
          <Button
            startIcon={<AddIcon />}
            onClick={() => addArrayItem('', 'socialActivities', {
              tahun: '',
              jenis_aktivitas: '',
              jabatan: '',
              organisasi: ''
            })}
            variant="outlined"
            size="small"
          >
            Tambah Aktivitas
          </Button>
        </Box>
        
        {candidateFormData.socialActivities.length === 0 && (
          <Paper sx={{ p: 3, textAlign: 'center', bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'action.hover' }}>
            <Typography variant="body2" color="text.secondary">
              Belum ada aktivitas sosial. Klik "Tambah Aktivitas" untuk menambah.
            </Typography>
          </Paper>
        )}
        
        {candidateFormData.socialActivities.map((activity, index) => (
          <Paper key={index} sx={{ p: 2, mb: 2, border: '1px solid', borderColor: 'grey.300' }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Chip label={`Aktivitas ${index + 1}`} color="warning" size="small" />
                  <IconButton
                    onClick={() => removeArrayItem('', 'socialActivities', index)}
                    color="error"
                    size="small"
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Grid>
              
              <Grid item xs={12} md={2}>
                <TextField
                  fullWidth
                  size="small"
                  label="Tahun"
                  value={activity.tahun}
                  onChange={(e) => handleArrayInputChange('', 'socialActivities', index, 'tahun', e.target.value)}
                />
              </Grid>
              
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Jenis Aktivitas"
                  value={activity.jenis_aktivitas}
                  onChange={(e) => handleArrayInputChange('', 'socialActivities', index, 'jenis_aktivitas', e.target.value)}
                />
              </Grid>
              
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="Jabatan"
                  value={activity.jabatan}
                  onChange={(e) => handleArrayInputChange('', 'socialActivities', index, 'jabatan', e.target.value)}
                />
              </Grid>
              
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="Organisasi"
                  value={activity.organisasi}
                  onChange={(e) => handleArrayInputChange('', 'socialActivities', index, 'organisasi', e.target.value)}
                />
              </Grid>
            </Grid>
          </Paper>
        ))}
      </Grid>
    </Grid>
  );

  const renderApplicationSkillsStep = () => (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WorkIcon color="primary" />
          Lamaran & Posisi yang Diinginkan
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Informasi tentang lamaran pekerjaan dan posisi yang diinginkan.
        </Typography>
      </Grid>
      
      {/* Alasan Melamar */}
      <Grid item xs={12}>
        <TextField
          fullWidth
          label="Tujuan / Alasan Anda Melamar"
          multiline
          rows={3}
          value={candidateFormData.applicationSkills.apply_reason}
          onChange={(e) => handleInputChange('applicationSkills', 'apply_reason', e.target.value)}
          helperText="Jelaskan alasan Anda melamar di perusahaan ini"
        />
      </Grid>
      
      {/* Inisiator */}
      <Grid item xs={12}>
        <FormControl component="fieldset">
          <FormLabel component="legend">Tindakan Anda atas saran:</FormLabel>
          <RadioGroup
            row
            value={candidateFormData.applicationSkills.apply_initiator}
            onChange={(e) => handleInputChange('applicationSkills', 'apply_initiator', e.target.value)}
          >
            <FormControlLabel value="Parents" control={<Radio />} label="Orang Tua" />
            <FormControlLabel value="Friends" control={<Radio />} label="Teman" />
            <FormControlLabel value="Family" control={<Radio />} label="Saudara" />
            <FormControlLabel value="Advertisement" control={<Radio />} label="Iklan" />
          </RadioGroup>
        </FormControl>
      </Grid>
      
      {/* Posisi & Gaji - Posisi sudah dipindah ke step 0 */}
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Gaji yang Diharapkan"
          value={candidateFormData.applicationSkills.expected_salary}
          onChange={(e) => handleInputChange('applicationSkills', 'expected_salary', e.target.value)}
          helperText="Contoh: Rp 15.000.000"
        />
      </Grid>
      
      <Grid item xs={12}>
        <TextField
          fullWidth
          label="Tunjangan dan Fasilitas yang Diharapkan"
          multiline
          rows={2}
          value={candidateFormData.applicationSkills.expected_benefits}
          onChange={(e) => handleInputChange('applicationSkills', 'expected_benefits', e.target.value)}
          helperText="Contoh: BPJS, THR, bonus kinerja"
        />
      </Grid>
      
      <Grid item xs={12}>
        <TextField
          fullWidth
          label="Tugas / Pekerjaan yang Anda Kuasai dengan Baik"
          multiline
          rows={3}
          value={candidateFormData.applicationSkills.skills}
          onChange={(e) => handleInputChange('applicationSkills', 'skills', e.target.value)}
          helperText="Jelaskan keahlian dan pekerjaan yang dikuasai"
        />
      </Grid>
      
      <Grid item xs={12}>
        <Divider sx={{ my: 2 }}>
          <Chip label="Kesediaan" />
        </Divider>
      </Grid>
      
      {/* Kesediaan (Switch) */}
      <Grid item xs={12} md={6}>
        <FormControlLabel
          control={
            <Switch
              checked={!!candidateFormData.applicationSkills.willing_medical}
              onChange={(e) => handleInputChange('applicationSkills', 'willing_medical', e.target.checked ? 1 : 0)}
            />
          }
          label="Bersedia mengikuti pemeriksaan medis lengkap"
        />
      </Grid>
      
      <Grid item xs={12} md={6}>
        <FormControlLabel
          control={
            <Switch
              checked={!!candidateFormData.applicationSkills.willing_psychology}
              onChange={(e) => handleInputChange('applicationSkills', 'willing_psychology', e.target.checked ? 1 : 0)}
            />
          }
          label="Bersedia mengikuti pemeriksaan psikologi"
        />
      </Grid>
      
      <Grid item xs={12} md={6}>
        <FormControlLabel
          control={
            <Switch
              checked={!!candidateFormData.applicationSkills.willing_relocate}
              onChange={(e) => handleInputChange('applicationSkills', 'willing_relocate', e.target.checked ? 1 : 0)}
            />
          }
          label="Bersedia ditugaskan ke luar kota"
        />
      </Grid>
      
      <Grid item xs={12} md={6}>
        <FormControlLabel
          control={
            <Switch
              checked={!!candidateFormData.applicationSkills.willing_trial}
              onChange={(e) => handleInputChange('applicationSkills', 'willing_trial', e.target.checked ? 1 : 0)}
            />
          }
          label="Bersedia menjalani masa percobaan 3 bulan"
        />
      </Grid>
      
      <Grid item xs={12} md={6}>
        <FormControlLabel
          control={
            <Switch
              checked={!!candidateFormData.applicationSkills.willing_transfer}
              onChange={(e) => handleInputChange('applicationSkills', 'willing_transfer', e.target.checked ? 1 : 0)}
            />
          }
          label="Bersedia dipindahkan ke luar kota"
        />
      </Grid>
      
      <Grid item xs={12} md={6}>
        <FormControlLabel
          control={
            <Switch
              checked={!!candidateFormData.applicationSkills.willing_rules}
              onChange={(e) => handleInputChange('applicationSkills', 'willing_rules', e.target.checked ? 1 : 0)}
            />
          }
          label="Bersedia mentaati peraturan dan tata tertib perusahaan"
        />
      </Grid>
    </Grid>
  );

  const renderSkillsStep = () => (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SchoolIcon color="primary" />
          Keterampilan & Kemampuan
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Informasi tentang keterampilan komputer, mengemudi, dan lainnya.
        </Typography>
      </Grid>
      
      {/* Komputer */}
      <Grid item xs={12} md={6}>
        <FormControlLabel
          control={
            <Switch
              checked={!!candidateFormData.applicationSkills.computer_skill}
              onChange={(e) => handleInputChange('applicationSkills', 'computer_skill', e.target.checked ? 1 : 0)}
            />
          }
          label="Dapat menggunakan dan mengoperasikan komputer"
        />
      </Grid>
      
      <Grid item xs={12} md={6}>
        <FormControl fullWidth>
          <InputLabel>Tingkat Mengetik</InputLabel>
          <Select
            value={candidateFormData.applicationSkills.typing_level}
            onChange={(e) => handleInputChange('applicationSkills', 'typing_level', e.target.value)}
            label="Tingkat Mengetik"
            disabled={!candidateFormData.applicationSkills.computer_skill}
          >
            <MenuItem value="Basic">Bisa</MenuItem>
            <MenuItem value="Intermediate">Sedang</MenuItem>
            <MenuItem value="Fluent">Lancar</MenuItem>
            <MenuItem value="Expert">Sangat Lancar</MenuItem>
          </Select>
        </FormControl>
      </Grid>
      
      <Grid item xs={12}>
        <TextField
          fullWidth
          label="Program yang Anda Kuasai"
          multiline
          rows={2}
          value={candidateFormData.applicationSkills.computer_programs}
          onChange={(e) => handleInputChange('applicationSkills', 'computer_programs', e.target.value)}
          disabled={!candidateFormData.applicationSkills.computer_skill}
          helperText="Contoh: Microsoft Office, Photoshop, AutoCAD"
        />
      </Grid>
      
      <Grid item xs={12}>
        <Divider sx={{ my: 2 }}>
          <Chip label="Kemampuan Mengemudi" />
        </Divider>
      </Grid>
      
      {/* Mengemudi */}
      <Grid item xs={12}>
        <FormControl component="fieldset">
          <FormLabel component="legend">Anda dapat mengendarai:</FormLabel>
          <FormGroup row>
            <FormControlLabel
              control={
                <Checkbox
                  checked={candidateFormData.applicationSkills.driving_ability?.includes('Motor')}
                  onChange={(e) => {
                    const current = candidateFormData.applicationSkills.driving_ability || '';
                    const newValue = e.target.checked 
                      ? (current ? `${current}, Motor` : 'Motor')
                      : current.replace(', Motor', '').replace('Motor, ', '').replace('Motor', '');
                    handleInputChange('applicationSkills', 'driving_ability', newValue);
                  }}
                />
              }
              label="Sepeda Motor"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={candidateFormData.applicationSkills.driving_ability?.includes('Mobil')}
                  onChange={(e) => {
                    const current = candidateFormData.applicationSkills.driving_ability || '';
                    const newValue = e.target.checked 
                      ? (current ? `${current}, Mobil` : 'Mobil')
                      : current.replace(', Mobil', '').replace('Mobil, ', '').replace('Mobil', '');
                    handleInputChange('applicationSkills', 'driving_ability', newValue);
                  }}
                />
              }
              label="Mobil"
            />
          </FormGroup>
        </FormControl>
      </Grid>
      
      <Grid item xs={12} md={6}>
        <FormControlLabel
          control={
            <Switch
              checked={!!candidateFormData.applicationSkills.vehicle_knowledge}
              onChange={(e) => handleInputChange('applicationSkills', 'vehicle_knowledge', e.target.checked ? 1 : 0)}
            />
          }
          label="Mengerti seluk-beluk motor bensin / diesel dan pelistrikan"
        />
      </Grid>
      
      <Grid item xs={12}>
        <Divider sx={{ my: 2 }}>
          <Chip label="Surat Izin Mengemudi (SIM)" />
        </Divider>
      </Grid>
      
      {/* SIM Section */}
      <Grid item xs={12}>
        <FormControlLabel
          control={
            <Switch
              checked={candidateFormData.personalData.has_sim}
              onChange={(e) => handleInputChange('personalData', 'has_sim', e.target.checked)}
            />
          }
          label="Anda memiliki SIM?"
          sx={{ mb: 2 }}
        />
      </Grid>
      
      {candidateFormData.personalData.has_sim && (
        <Grid container spacing={2}>
          {/* SIM C */}
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 2, border: '1px solid', borderColor: 'grey.300' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, textAlign: 'center' }}>
                SIM C
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={candidateFormData.personalData.sim_c_valid || false}
                        onChange={(e) => {
                          const isValid = e.target.checked;
                          handleInputChange('personalData', 'sim_c_valid', isValid);
                          if (!isValid) {
                            handleInputChange('personalData', 'sim_c_number', '');
                            handleInputChange('personalData', 'sim_c_valid_until', '');
                          }
                        }}
                      />
                    }
                    label="Valid"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    size="small"
                    label="No. SIM C"
                    value={candidateFormData.personalData.sim_c_number}
                    onChange={(e) => handleInputChange('personalData', 'sim_c_number', e.target.value)}
                    disabled={!candidateFormData.personalData.sim_c_valid}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Masa Berlaku"
                    type="date"
                    value={candidateFormData.personalData.sim_c_valid_until}
                    onChange={(e) => handleInputChange('personalData', 'sim_c_valid_until', e.target.value)}
                    disabled={!candidateFormData.personalData.sim_c_valid}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              </Grid>
            </Paper>
          </Grid>
          
          {/* SIM A */}
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 2, border: '1px solid', borderColor: 'grey.300' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, textAlign: 'center' }}>
                SIM A
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={candidateFormData.personalData.sim_a_valid || false}
                        onChange={(e) => {
                          const isValid = e.target.checked;
                          handleInputChange('personalData', 'sim_a_valid', isValid);
                          if (!isValid) {
                            handleInputChange('personalData', 'sim_a_number', '');
                            handleInputChange('personalData', 'sim_a_valid_until', '');
                          }
                        }}
                      />
                    }
                    label="Valid"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    size="small"
                    label="No. SIM A"
                    value={candidateFormData.personalData.sim_a_number}
                    onChange={(e) => handleInputChange('personalData', 'sim_a_number', e.target.value)}
                    disabled={!candidateFormData.personalData.sim_a_valid}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Masa Berlaku"
                    type="date"
                    value={candidateFormData.personalData.sim_a_valid_until}
                    onChange={(e) => handleInputChange('personalData', 'sim_a_valid_until', e.target.value)}
                    disabled={!candidateFormData.personalData.sim_a_valid}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              </Grid>
            </Paper>
          </Grid>
          
          {/* SIM B */}
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 2, border: '1px solid', borderColor: 'grey.300' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, textAlign: 'center' }}>
                SIM B
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={candidateFormData.personalData.sim_b_valid || false}
                        onChange={(e) => {
                          const isValid = e.target.checked;
                          handleInputChange('personalData', 'sim_b_valid', isValid);
                          if (!isValid) {
                            handleInputChange('personalData', 'sim_b_number', '');
                            handleInputChange('personalData', 'sim_b_valid_until', '');
                          }
                        }}
                      />
                    }
                    label="Valid"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    size="small"
                    label="No. SIM B"
                    value={candidateFormData.personalData.sim_b_number}
                    onChange={(e) => handleInputChange('personalData', 'sim_b_number', e.target.value)}
                    disabled={!candidateFormData.personalData.sim_b_valid}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Masa Berlaku"
                    type="date"
                    value={candidateFormData.personalData.sim_b_valid_until}
                    onChange={(e) => handleInputChange('personalData', 'sim_b_valid_until', e.target.value)}
                    disabled={!candidateFormData.personalData.sim_b_valid}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        </Grid>
      )}
    </Grid>
  );

  const renderHealthStep = () => (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <HealthIcon color="primary" />
          Kesehatan & Umum
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Informasi tentang kesehatan dan kondisi umum kandidat.
        </Typography>
      </Grid>
      
      {/* Pertanyaan Umum */}
      <Grid item xs={12} md={6}>
        <FormControlLabel
          control={
            <Switch
              checked={!!candidateFormData.generalHealth.ever_fired}
              onChange={(e) => handleInputChange('generalHealth', 'ever_fired', e.target.checked ? 1 : 0)}
            />
          }
          label="Pernah diberhentikan dari pekerjaan karena prestasi kurang baik"
        />
      </Grid>
      
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Sumber Nasihat untuk Persoalan Pekerjaan"
          value={candidateFormData.generalHealth.work_advice_source}
          onChange={(e) => handleInputChange('generalHealth', 'work_advice_source', e.target.value)}
          helperText="Contoh: Atasan, Teman sekerja"
        />
      </Grid>
      
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Sumber Nasihat untuk Persoalan Pribadi"
          value={candidateFormData.generalHealth.personal_advice_source}
          onChange={(e) => handleInputChange('generalHealth', 'personal_advice_source', e.target.value)}
          helperText="Contoh: Keluarga, Teman dekat"
        />
      </Grid>
      
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Jelaskan Sumber Nasihat Pribadi"
          value={candidateFormData.generalHealth.personal_advice_note}
          onChange={(e) => handleInputChange('generalHealth', 'personal_advice_note', e.target.value)}
        />
      </Grid>
      
      <Grid item xs={12}>
        <TextField
          fullWidth
          label="Reaksi Anda apabila Ditegur oleh Atasan"
          multiline
          rows={2}
          value={candidateFormData.generalHealth.reaction_to_criticism}
          onChange={(e) => handleInputChange('generalHealth', 'reaction_to_criticism', e.target.value)}
        />
      </Grid>
      
      <Grid item xs={12}>
        <Divider sx={{ my: 2 }}>
          <Chip label="Kesehatan" />
        </Divider>
      </Grid>
      
      {/* Kesehatan */}
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Sakit yang Sering Anda Alami"
          value={candidateFormData.generalHealth.common_illness}
          onChange={(e) => handleInputChange('generalHealth', 'common_illness', e.target.value)}
        />
      </Grid>
      
      <Grid item xs={12} md={6}>
        <FormControlLabel
          control={
            <Switch
              checked={!!candidateFormData.generalHealth.serious_illness}
              onChange={(e) => handleInputChange('generalHealth', 'serious_illness', e.target.checked ? 1 : 0)}
            />
          }
          label="Pernah sakit keras perlu perawatan khusus"
        />
      </Grid>
      
      <Grid item xs={12}>
        <TextField
          fullWidth
          label="Jelaskan Sakit Keras"
          value={candidateFormData.generalHealth.serious_illness_desc}
          onChange={(e) => handleInputChange('generalHealth', 'serious_illness_desc', e.target.value)}
          disabled={!candidateFormData.generalHealth.serious_illness}
        />
      </Grid>
      
      <Grid item xs={12}>
        <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
          Apakah Anda menderita / pernah menderita:
        </Typography>
      </Grid>
      
      <Grid item xs={12} md={4}>
        <FormControlLabel
          control={
            <Switch
              checked={!!candidateFormData.generalHealth.eye_disease}
              onChange={(e) => handleInputChange('generalHealth', 'eye_disease', e.target.checked ? 1 : 0)}
            />
          }
          label="Penyakit Mata"
        />
      </Grid>
      
      <Grid item xs={12} md={4}>
        <FormControlLabel
          control={
            <Switch
              checked={!!candidateFormData.generalHealth.ear_disease}
              onChange={(e) => handleInputChange('generalHealth', 'ear_disease', e.target.checked ? 1 : 0)}
            />
          }
          label="Penyakit Telinga"
        />
      </Grid>
      
      <Grid item xs={12} md={4}>
        <FormControlLabel
          control={
            <Switch
              checked={!!candidateFormData.generalHealth.hypertension}
              onChange={(e) => handleInputChange('generalHealth', 'hypertension', e.target.checked ? 1 : 0)}
            />
          }
          label="Penyakit Darah Tinggi"
        />
      </Grid>
      
      <Grid item xs={12} md={4}>
        <FormControlLabel
          control={
            <Switch
              checked={!!candidateFormData.generalHealth.lung_disease}
              onChange={(e) => handleInputChange('generalHealth', 'lung_disease', e.target.checked ? 1 : 0)}
            />
          }
          label="Penyakit Paru-paru"
        />
      </Grid>
      
      <Grid item xs={12} md={4}>
        <FormControlLabel
          control={
            <Switch
              checked={!!candidateFormData.generalHealth.diabetes}
              onChange={(e) => handleInputChange('generalHealth', 'diabetes', e.target.checked ? 1 : 0)}
            />
          }
          label="Penyakit Gula"
        />
      </Grid>
      
      <Grid item xs={12} md={4}>
        <FormControlLabel
          control={
            <Switch
              checked={!!candidateFormData.generalHealth.heart_disease}
              onChange={(e) => handleInputChange('generalHealth', 'heart_disease', e.target.checked ? 1 : 0)}
            />
          }
          label="Penyakit Jantung"
        />
      </Grid>
      
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="General Check Up Terakhir"
          type="date"
          value={candidateFormData.generalHealth.last_checkup}
          onChange={(e) => handleInputChange('generalHealth', 'last_checkup', e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
      </Grid>
      
      <Grid item xs={12}>
        <Divider sx={{ my: 2 }}>
          <Chip label="Kelebihan & Kekurangan" />
        </Divider>
      </Grid>
      
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Kelebihan Anda"
          multiline
          rows={3}
          value={candidateFormData.generalHealth.strength}
          onChange={(e) => handleInputChange('generalHealth', 'strength', e.target.value)}
          helperText="Sebutkan kelebihan yang mendukung pekerjaan"
        />
      </Grid>
      
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Kekurangan Anda"
          multiline
          rows={3}
          value={candidateFormData.generalHealth.weakness}
          onChange={(e) => handleInputChange('generalHealth', 'weakness', e.target.value)}
          helperText="Sebutkan kekurangan yang perlu diperhatikan"
        />
      </Grid>
    </Grid>
  );

  const renderOthersStep = () => (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <InfoIcon color="primary" />
          Kontak Darurat & Penjamin
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Informasi kontak darurat, referensi, dan penjamin.
        </Typography>
      </Grid>
      
      {/* Kontak Darurat */}
      <Grid item xs={12}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="subtitle1" fontWeight={600}>
          Kontak Darurat
        </Typography>
        <Button
          startIcon={<AddIcon />}
          onClick={() => addArrayItem('others', 'emergency_relations', {
            nama: '',
            usia: '',
            alamat: '',
            no_telepon: '',
            hubungan: '',
            keterangan: ''
          })}
          variant="outlined"
            size="small"
        >
          Tambah Kontak Darurat
        </Button>
        </Box>
        
        {candidateFormData.others.emergency_relations.length === 0 && (
          <Paper sx={{ p: 3, textAlign: 'center', bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'action.hover' }}>
            <Typography variant="body2" color="text.secondary">
              Belum ada kontak darurat. Klik "Tambah Kontak Darurat" untuk menambah.
            </Typography>
          </Paper>
        )}
        
        {candidateFormData.others.emergency_relations.map((contact, index) => (
          <Paper key={index} sx={{ p: 2, mb: 2, border: '1px solid', borderColor: 'grey.300' }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Chip label={`Kontak Darurat ${index + 1}`} color="error" size="small" />
                  <IconButton
                    onClick={() => removeArrayItem('others', 'emergency_relations', index)}
                    color="error"
                    size="small"
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Grid>
              
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="Nama"
                  value={contact.nama}
                  onChange={(e) => handleArrayInputChange('others', 'emergency_relations', index, 'nama', e.target.value)}
                />
              </Grid>
              
              <Grid item xs={12} md={2}>
                <TextField
                  fullWidth
                  size="small"
                  label="Usia"
                  type="number"
                  value={contact.usia}
                  onChange={(e) => handleArrayInputChange('others', 'emergency_relations', index, 'usia', e.target.value)}
                />
              </Grid>
              
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="Alamat"
                  value={contact.alamat}
                  onChange={(e) => handleArrayInputChange('others', 'emergency_relations', index, 'alamat', e.target.value)}
                />
              </Grid>
              
              <Grid item xs={12} md={2}>
                <TextField
                  fullWidth
                  size="small"
                  label="No. Telepon"
                  value={contact.no_telepon}
                  onChange={(e) => handleArrayInputChange('others', 'emergency_relations', index, 'no_telepon', e.target.value)}
                />
              </Grid>
              
              <Grid item xs={12} md={2}>
                <TextField
                  fullWidth
                  size="small"
                  label="Hubungan"
                  value={contact.hubungan}
                  onChange={(e) => handleArrayInputChange('others', 'emergency_relations', index, 'hubungan', e.target.value)}
                />
              </Grid>
            </Grid>
          </Paper>
        ))}
      </Grid>
      
      <Grid item xs={12}>
        <Divider sx={{ my: 2 }} />
    </Grid>

      {/* Eks Atasan/Teman */}
      <Grid item xs={12}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="subtitle1" fontWeight={600}>
            Keluarga / Eks Atasan / Teman (Referensi)
          </Typography>
          <Button
            startIcon={<AddIcon />}
            onClick={() => addArrayItem('others', 'former_relations', {
              nama: '',
              usia: '',
              alamat: '',
              no_telepon: '',
              hubungan: '',
              keterangan: ''
            })}
            variant="outlined"
            size="small"
          >
            Tambah Referensi
          </Button>
        </Box>
        
        {candidateFormData.others.former_relations.length === 0 && (
          <Paper sx={{ p: 3, textAlign: 'center', bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'action.hover' }}>
            <Typography variant="body2" color="text.secondary">
              Belum ada referensi. Klik "Tambah Referensi" untuk menambah.
            </Typography>
          </Paper>
        )}
        
        {candidateFormData.others.former_relations.map((reference, index) => (
          <Paper key={index} sx={{ p: 2, mb: 2, border: '1px solid', borderColor: 'grey.300' }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Chip label={`Referensi ${index + 1}`} color="info" size="small" />
                <IconButton
                    onClick={() => removeArrayItem('others', 'former_relations', index)}
                  color="error"
                    size="small"
                >
                    <DeleteIcon fontSize="small" />
                </IconButton>
                </Box>
              </Grid>
              
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="Nama"
                  value={reference.nama}
                  onChange={(e) => handleArrayInputChange('others', 'former_relations', index, 'nama', e.target.value)}
                />
              </Grid>
              
              <Grid item xs={12} md={2}>
                <TextField
                  fullWidth
                  size="small"
                  label="Usia"
                  type="number"
                  value={reference.usia}
                  onChange={(e) => handleArrayInputChange('others', 'former_relations', index, 'usia', e.target.value)}
                />
              </Grid>
              
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="Alamat"
                  value={reference.alamat}
                  onChange={(e) => handleArrayInputChange('others', 'former_relations', index, 'alamat', e.target.value)}
                />
              </Grid>
              
              <Grid item xs={12} md={2}>
                <TextField
                  fullWidth
                  size="small"
                  label="No. Telepon"
                  value={reference.no_telepon}
                  onChange={(e) => handleArrayInputChange('others', 'former_relations', index, 'no_telepon', e.target.value)}
                />
              </Grid>
              
              <Grid item xs={12} md={2}>
                <TextField
                  fullWidth
                  size="small"
                  label="Hubungan"
                  value={reference.hubungan}
                  onChange={(e) => handleArrayInputChange('others', 'former_relations', index, 'hubungan', e.target.value)}
                />
              </Grid>
            </Grid>
          </Paper>
        ))}
      </Grid>
      
      <Grid item xs={12}>
        <Divider sx={{ my: 2 }} />
      </Grid>
      
      {/* Penjamin */}
      <Grid item xs={12}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="subtitle1" fontWeight={600}>
            Penjamin di PT Eureka Group
          </Typography>
          <Button
            startIcon={<AddIcon />}
            onClick={() => addArrayItem('others', 'guarantors', {
              nama: '',
              usia: '',
              alamat: '',
              no_telepon: '',
              hubungan: '',
              keterangan: ''
            })}
            variant="outlined"
            size="small"
          >
            Tambah Penjamin
          </Button>
        </Box>
        
        {candidateFormData.others.guarantors.length === 0 && (
          <Paper sx={{ p: 3, textAlign: 'center', bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'action.hover' }}>
            <Typography variant="body2" color="text.secondary">
              Belum ada penjamin. Klik "Tambah Penjamin" untuk menambah.
            </Typography>
          </Paper>
        )}
        
        {candidateFormData.others.guarantors.map((guarantor, index) => (
          <Paper key={index} sx={{ p: 2, mb: 2, border: '1px solid', borderColor: 'grey.300' }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Chip label={`Penjamin ${index + 1}`} color="success" size="small" />
                  <IconButton
                    onClick={() => removeArrayItem('others', 'guarantors', index)}
                    color="error"
                    size="small"
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Grid>
              
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="Nama"
                  value={guarantor.nama}
                  onChange={(e) => handleArrayInputChange('others', 'guarantors', index, 'nama', e.target.value)}
                />
              </Grid>
              
              <Grid item xs={12} md={2}>
                <TextField
                  fullWidth
                  size="small"
                  label="Usia"
                  type="number"
                  value={guarantor.usia}
                  onChange={(e) => handleArrayInputChange('others', 'guarantors', index, 'usia', e.target.value)}
                />
              </Grid>
              
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="Alamat"
                  value={guarantor.alamat}
                  onChange={(e) => handleArrayInputChange('others', 'guarantors', index, 'alamat', e.target.value)}
                />
              </Grid>
              
              <Grid item xs={12} md={2}>
                <TextField
                  fullWidth
                  size="small"
                  label="No. Telepon"
                  value={guarantor.no_telepon}
                  onChange={(e) => handleArrayInputChange('others', 'guarantors', index, 'no_telepon', e.target.value)}
                />
              </Grid>
              
              <Grid item xs={12} md={2}>
                <TextField
                  fullWidth
                  size="small"
                  label="Hubungan"
                  value={guarantor.hubungan}
                  onChange={(e) => handleArrayInputChange('others', 'guarantors', index, 'hubungan', e.target.value)}
                />
              </Grid>
            </Grid>
          </Paper>
        ))}
      </Grid>
    </Grid>
  );

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return renderPersonalDataStep();
      case 1:
        return renderPersonalDataAdvancedStep();
      case 2:
        return renderEducationCoursesStep();
      case 3:
        return renderWorkHistoryStep();
      case 4:
        return renderFamilyActivitiesStep();
      case 5:
        return renderApplicationSkillsStep();
      case 6:
        return renderSkillsStep();
      case 7:
        return renderHealthStep();
      case 8:
        return renderOthersStep();
      default:
        return 'Unknown step';
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Card>
        <CardHeader
          title={
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <IconButton
                onClick={() => navigate('/kontak/candidate')}
                sx={{ mr: 2 }}
              >
                <ArrowBackIcon />
              </IconButton>
              <ContactsIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              <Typography variant="h4" component="h1">
                Tambah Kandidat Baru
              </Typography>
            </Box>
          }
        />
        <Divider />
        <CardContent>
          {/* Mode selector: Simple form vs Full multi-step form */}
          <Tabs value={formMode} onChange={(e, v) => setFormMode(v)} sx={{ mb: 2 }}>
            <Tab label="Simple Form" value="simple" />
            <Tab label="Full Form" value="full" />
          </Tabs>
          {formMode === 'full' ? (
            <>
              <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
                {steps.map((label, index) => (
                  <Step key={label}>
                    <StepLabel>{label}</StepLabel>
                  </Step>
                ))}
              </Stepper>

              <Paper sx={{ p: 3, mb: 3 }}>
                {renderStepContent(activeStep)}
              </Paper>

              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Button
                  disabled={activeStep === 0}
                  onClick={handleBack}
                  startIcon={<BackIcon />}
                >
                  Kembali
                </Button>
                
                <Box>
                  {activeStep === steps.length - 1 ? (
                    <Button
                      variant="contained"
                      onClick={handleSubmit}
                      disabled={loading}
                      startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
                    >
                      {loading ? 'Menyimpan...' : 'Simpan Kandidat'}
                    </Button>
                  ) : (
                    <Button
                      variant="contained"
                      onClick={handleNext}
                      endIcon={<NextIcon />}
                    >
                      Selanjutnya
                    </Button>
                  )}
                </Box>
              </Box>
            </>
          ) : (
            <>
              {/* Simple form: only render personal data basic step and a create button */}
              <Paper sx={{ p: 3, mb: 3 }}>
                {renderPersonalDataStep()}
              </Paper>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Button onClick={() => navigate('/kontak/candidate')} startIcon={<ArrowBackIcon />}>Batal</Button>
                <Button variant="contained" onClick={handleSimpleSubmit} disabled={loading} startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}> {loading ? 'Menyimpan...' : 'Buat Kandidat'}</Button>
              </Box>
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default AddCandidate;
